import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faShieldAlt, faCheckCircle, faExclamationTriangle, faTimes, faArrowRight
} from '@fortawesome/free-solid-svg-icons';
import { kycAPI } from '../services/api';
import './KYCStatusBanner.css';

const KYCStatusBanner = ({ onNavigateToKYC }) => {
  const [kycStatus, setKycStatus] = useState(null);
  const [isVisible, setIsVisible] = useState(true);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    checkKYCStatus();
  }, []);

  const checkKYCStatus = async () => {
    try {
      const response = await kycAPI.getStatus();

      if (response && response.data) {
        const { steps, verification_status } = response.data;

        // Normalize steps into the shape getMessage() expects
        const normalized = {
          email: {
            verified: steps?.email?.verified,
            status: steps?.email?.verified ? 'verified' : 'pending'
          },
          identity: {
            verified: steps?.nin?.verified,
            status: steps?.nin?.status || 'not_started'
          },
          business: {
            verified: steps?.business?.verified,
            status: steps?.business?.status || 'not_started'
          },
          address: {
            verified: steps?.address?.verified,
            status: steps?.address?.verified ? 'verified' : 'not_started'
          }
        };
        setKycStatus(normalized);

        const allVerified =
          verification_status === 'verified' || (
            normalized.email.verified &&
            normalized.identity.verified &&
            normalized.business.verified &&
            normalized.address.verified
          );

        if (allVerified) {
          setIsVisible(false);
        }
      } else {
        setIsVisible(true);
      }
    } catch (error) {
      setIsVisible(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setIsVisible(false);
    // Store in sessionStorage so it doesn't show again this session
    sessionStorage.setItem('kycBannerDismissed', 'true');
  };

  const getMessage = () => {
    if (!kycStatus) {
      return {
        type: 'warning',
        icon: faExclamationTriangle,
        title: 'Complete KYC Verification',
        message: 'Verify your account to unlock all features and increase trust with buyers/sellers.',
        action: 'Start Verification'
      };
    }

    const { email, identity, business, address } = kycStatus;

    // Check for any pending reviews
    const hasPendingReview =
      email?.status === 'pending_review' ||
      identity?.status === 'pending_review' ||
      business?.status === 'pending_review' ||
      address?.status === 'pending_review';

    if (hasPendingReview) {
      return {
        type: 'info',
        icon: faShieldAlt,
        title: 'KYC Under Review',
        message: 'Your verification documents are being reviewed. This usually takes 24-48 hours.',
        action: 'View Status'
      };
    }

    // Check for rejected
    const hasRejected =
      email?.status === 'rejected' ||
      identity?.status === 'rejected' ||
      business?.status === 'rejected' ||
      address?.status === 'rejected';

    if (hasRejected) {
      return {
        type: 'error',
        icon: faExclamationTriangle,
        title: 'Action Required',
        message: 'Some verification documents were rejected. Please resubmit to complete verification.',
        action: 'Fix Issues'
      };
    }

    // Not started or in progress
    const completedSteps = [
      email?.verified,
      identity?.verified,
      address?.verified,
      business?.verified
    ].filter(Boolean).length;

    return {
      type: 'warning',
      icon: faExclamationTriangle,
      title: 'Complete Your Verification',
      message: `${completedSteps} of 4 steps completed. Finish verification to unlock all features.`,
      action: 'Continue Verification'
    };
  };

  if (isLoading || !isVisible) return null;

  // Check if banner was dismissed this session
  if (sessionStorage.getItem('kycBannerDismissed') === 'true') return null;

  const { type, icon, title, message, action } = getMessage();

  return (
    <div className={`kyc-banner kyc-banner-${type}`}>
      <div className="kyc-banner-icon">
        <FontAwesomeIcon icon={icon} />
      </div>
      <div className="kyc-banner-content">
        <h4 className="kyc-banner-title">{title}</h4>
        <p className="kyc-banner-message">{message}</p>
      </div>
      <div className="kyc-banner-actions">
        <button className="kyc-banner-btn" onClick={onNavigateToKYC}>
          {action}
          <FontAwesomeIcon icon={faArrowRight} style={{ marginLeft: '0.5rem' }} />
        </button>
        <button className="kyc-banner-close" onClick={handleClose}>
          <FontAwesomeIcon icon={faTimes} />
        </button>
      </div>
    </div>
  );
};

export default KYCStatusBanner;
